import { useEffect } from 'react'

function closestActionTarget(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null
  const el = target.closest('button, [role="button"], a.btn-primary, a.btn-secondary')
  return el instanceof HTMLElement ? el : null
}

function isDisabled(el: HTMLElement): boolean {
  return (
    (el instanceof HTMLButtonElement && el.disabled) ||
    el.getAttribute('aria-disabled') === 'true' ||
    el.hasAttribute('disabled')
  )
}

function isBusy(el: HTMLElement): boolean {
  return el.classList.contains('sa-btn-busy') || el.getAttribute('aria-busy') === 'true'
}

/**
 * Instant press feedback on every button, before React handlers / API work start.
 * Swallows a second click while the control is already marked busy.
 */
export default function ImmediateActionFeedback() {
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const el = closestActionTarget(e.target)
      if (!el || isDisabled(el) || e.button !== 0) return
      // A finished action used to leave this flag set. The next press then looked
      // like a duplicate click and was swallowed, so Save did nothing until refresh.
      if (!isBusy(el)) delete el.dataset.saClickStarted
      el.classList.add('sa-btn-armed')
    }

    const clearArmed = (e: Event) => {
      const el = closestActionTarget(e.target)
      el?.classList.remove('sa-btn-armed')
    }

    const onClickCapture = (e: MouseEvent) => {
      const el = closestActionTarget(e.target)
      if (!el || isDisabled(el)) return
      if (isBusy(el) && el.dataset.saClickStarted === '1') {
        e.preventDefault()
        e.stopPropagation()
        return
      }
      if (isBusy(el)) {
        el.dataset.saClickStarted = '1'
      } else {
        delete el.dataset.saClickStarted
      }
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('pointerup', clearArmed, true)
    document.addEventListener('pointercancel', clearArmed, true)
    document.addEventListener('click', onClickCapture, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('pointerup', clearArmed, true)
      document.removeEventListener('pointercancel', clearArmed, true)
      document.removeEventListener('click', onClickCapture, true)
    }
  }, [])

  return null
}
